import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, Observable } from 'rxjs';

import { DeviceUiIntellimixDeviceBehaviorPlugin, IntellimixType } from '@shure/device-ui/intellimix/data-access';
import {
	DeviceUiSchematicsDeviceBehaviorPlugin,
	SchematicsDeviceChannelDspKind
} from '@shure/device-ui/schematics/data-access';
import { DspType } from '@shure/device-ui/shared/dsp/data-access';
import { DeviceUiPage, DeviceUiRoute } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import {
	AnalogOutputMode,
	ChannelGroup,
	DeviceConferenceOperationMode,
	DeviceModel
} from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class MxcwApxDeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiIntellimixDeviceBehaviorPlugin,
		DeviceUiSchematicsDeviceBehaviorPlugin
{
	public readonly models: DeviceModel[] = [DeviceModel.MXCWAPX];

	constructor(protected readonly transloco: TranslocoService) {}

	public getAvailablePages$(_model: DeviceModel): Observable<DeviceUiPage[] | undefined> {
		return combineLatest([
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.direct-channels'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.inputs'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.intellimix'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.matrix-mixer'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.outputs'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.schematic'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.settings')
		]).pipe(
			map(
				([
					directChannelsLabel,
					inputsLabel,
					intellimixLabel,
					matrixMixerLabel,
					outputsLabel,
					schematicLabel,
					settingsLabel
				]) => [
					{ route: DeviceUiRoute.DirectChannels, label: directChannelsLabel },
					{ route: DeviceUiRoute.Inputs, label: inputsLabel },
					{ route: DeviceUiRoute.Intellimix, label: intellimixLabel },
					{ route: DeviceUiRoute.Matrixmixer, label: matrixMixerLabel },
					{ route: DeviceUiRoute.Outputs, label: outputsLabel },
					{ route: DeviceUiRoute.Schematic, label: schematicLabel },
					{ route: DeviceUiRoute.Settings, label: settingsLabel }
				]
			)
		);
	}

	public getDefaultRoute(_model: DeviceModel): DeviceUiRoute | undefined {
		return DeviceUiRoute.Settings;
	}

	public getIntellimixType(_model: DeviceModel): IntellimixType | undefined {
		return IntellimixType.Advanced;
	}

	public revertAutomixerDefaultsCapability(_model: DeviceModel): boolean {
		return true;
	}

	public hideAgcMetering(_model: DeviceModel): boolean {
		return false;
	}

	public hideDspButtons(_model: DeviceModel): boolean {
		return false;
	}

	public getIntellimixChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> | undefined {
		return undefined;
	}

	public getInputChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [ChannelGroup.DanteInput, ChannelGroup.Generic, ChannelGroup.DanteAuxInput, ChannelGroup.UsbInput];
	}

	public getFusionEnabledInputChannelGroups(model: DeviceModel): ChannelGroup[] {
		return [
			...this.getInputChannelGroups(model).slice(0, 1),
			ChannelGroup.DanteMicInput,
			...this.getInputChannelGroups(model).slice(1)
		];
	}

	public getDirectChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [ChannelGroup.DirectOutputDanteMicInput, ChannelGroup.DirectOutput];
	}

	public getFusionEnabledDirectChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [ChannelGroup.DirectOutputDanteMicInput, ChannelGroup.DirectOutput];
	}

	public getOutputChannelGroups(_model: DeviceModel, _analogOutputMode?: AnalogOutputMode): ChannelGroup[] {
		return [
			ChannelGroup.DanteOutput,
			ChannelGroup.AnalogOutput,
			ChannelGroup.DanteLineOutput,
			ChannelGroup.UsbOutput,
			ChannelGroup.Back,
			ChannelGroup.AuracastOutput
		];
	}

	public shouldResetOuputGroupIndex(_model: DeviceModel): boolean {
		return false;
	}

	public getAutomixedChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [ChannelGroup.DanteMicInput, ChannelGroup.Generic];
	}

	public getSchematicOutputChannelDspSortingMap(_model: DeviceModel): Map<SchematicsDeviceChannelDspKind, number> {
		return new Map<SchematicsDeviceChannelDspKind, number>([['equalizer', 1]]);
	}

	public connectSchematicsAutomixerToSecondChannelGroup(_model: DeviceModel): boolean {
		return true;
	}

	public isFusionEnabled(
		_model: DeviceModel,
		_mode: DeviceConferenceOperationMode,
		_firmwareVersion: string,
		_virtual: boolean
	): boolean {
		return false;
	}
}
